import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowRight, ArrowUp, Check, ChevronDown, ClipboardCheck, FileSpreadsheet, FileText, Loader2, PanelLeft, Paperclip, RotateCw, Search, Settings2, SquarePen, Trash2, X } from "lucide-react";
import { api } from "../api";
import { local } from "../storage";
import { useAuth } from "../auth";
import { useApi } from "../hooks";
import ChatMessage, { AnalystMark } from "../components/ChatMessage";
import { ErrorState } from "../components/Ui";
import { readStream } from "../ads-ai";
import "../polish/jarvis.css";
import { attachmentUrl, uploadAttachment } from "../jarvis-upload";
import { JARVIS_ACCEPT, JARVIS_MAX_BYTES, JARVIS_MAX_FILES, JARVIS_STARTERS, fileSizeLabel, groupModels, modelLabel, startingModel, type JarvisAttachment, groupJarvisSessions, jarvisTitle, jarvisWhen, providerLabel, type JarvisAction, type JarvisEvent, type JarvisMessage, type JarvisSession, type JarvisSettings } from "../jarvis";

type Session = JarvisSession;
const LAST_KEY = "nl-aos-jarvis-session";
const RAIL_KEY = "nl-aos-jarvis-rail";
const MODEL_KEY = "nl-aos-jarvis-model";
/** A file in the message box: uploading, ready (has its saved id) or refused. */
type Pending = { key: string; name: string; size: number; preview?: string; status: "uploading" | "ready" | "error"; file?: JarvisAttachment; error?: string };
const POLL_MS = 6000;

/** Jarvis: the agency-wide assistant. Reads any staff page's data as you, and adds tasks when asked. */
export default function Jarvis() {
  const { user } = useAuth();
  const admin = user?.role === "superadmin";
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const sessionId = Number(params.get("session")) || null;
  const sessions = useApi<Session[]>("/api/jarvis/sessions");
  const settings = useApi<JarvisSettings>("/api/jarvis/settings");
  const [messages, setMessages] = useState<JarvisMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState(params.get("go") ? "" : params.get("ask") || "");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState<{ steps: string[]; actions: JarvisAction[] }>({ steps: [], actions: [] });
  const [failure, setFailure] = useState<{ text: string; message: string } | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [railHidden, setRailHidden] = useState(() => local.get(RAIL_KEY) === "hidden");
  const [search, setSearch] = useState("");
  const [removing, setRemoving] = useState<number | null>(null);
  const [pending, setPending] = useState<Pending[]>([]);
  const [dragging, setDragging] = useState(false);
  const [modelPick, setModelPick] = useState<string | null>(() => local.get(MODEL_KEY));
  const picker = useRef<HTMLInputElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const drawerToggle = useRef<HTMLButtonElement>(null);
  const followAnswer = useRef(true);
  const skipLoad = useRef(false);
  const alive = useRef(true);
  const sending = useRef(false);
  const reopened = useRef(false);
  const asked = useRef(false);
  const stream = useRef<AbortController | null>(null);
  const loader = useRef<AbortController | null>(null);
  const poll = useRef<number | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; stream.current?.abort(); loader.current?.abort(); if (poll.current) window.clearTimeout(poll.current); };
  }, []);

  const load = useCallback(async (id: number, quiet = false) => {
    loader.current?.abort();
    if (poll.current) { window.clearTimeout(poll.current); poll.current = null; }
    const controller = new AbortController(); loader.current = controller;
    if (!quiet) { setLoading(true); setLoadError(""); }
    try {
      const r = await api<{ session?: { running?: boolean }; messages: JarvisMessage[] }>(`/api/jarvis/sessions/${id}`, { signal: controller.signal });
      if (!alive.current || controller.signal.aborted) return;
      setMessages(r.messages);
      const last = r.messages[r.messages.length - 1];
      const unanswered = last?.role === "user"; const running = Boolean(r.session?.running);
      setWaiting(unanswered && running);
      const question = [...r.messages].reverse().find((m) => m.role === "user");
      setFailure(unanswered && !running ? { text: last.content, message: "This answer did not finish. Ask again to get it." }
        : last?.failed && question ? { text: question.content, message: last.content } : null);
      if (unanswered && running) poll.current = window.setTimeout(() => { if (alive.current) void load(id, true); }, POLL_MS);
    } catch (e) {
      if (!alive.current || controller.signal.aborted) return;
      if (!quiet) setLoadError(e instanceof Error ? e.message : "The conversation could not load.");
    } finally {
      if (loader.current === controller) { loader.current = null; if (alive.current && !quiet) setLoading(false); }
    }
  }, []);

  useEffect(() => {
    followAnswer.current = true;
    if (sessionId) local.set(LAST_KEY, String(sessionId));
    if (skipLoad.current) { skipLoad.current = false; return; }
    stream.current?.abort(); stream.current = null; sending.current = false;
    setBusy(false); setWaiting(false); setLive({ steps: [], actions: [] }); setFailure(null); setLoadError("");
    if (sessionId) void load(sessionId); else { setMessages([]); setLoading(false); }
  }, [sessionId, load]);

  // Coming back reopens the conversation you had open, unless the overview sent a new question.
  useEffect(() => {
    if (reopened.current || !sessions.data) return;
    reopened.current = true;
    if (sessionId || params.get("ask")) return;
    const stored = Number(local.get(LAST_KEY)) || null;
    const pick = sessions.data.find((x) => x.id === stored) || sessions.data.find((x) => x.running);
    if (stored && !pick) local.remove(LAST_KEY);
    if (!pick) return;
    const next = new URLSearchParams(params); next.set("session", String(pick.id)); setParams(next, { replace: true });
  }, [sessions.data, sessionId, params, setParams]);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const welcome = !sessionId && !messages.length && !busy;
    if (welcome || followAnswer.current) el.scrollTo({ top: welcome ? 0 : el.scrollHeight, behavior: "auto" });
  }, [sessionId, messages.length, live.steps.length, live.actions.length, busy]);

  async function send(text: string, withFiles = true) {
    const t = text.trim();
    const ready = withFiles ? pending.filter((f) => f.status === "ready" && f.file).map((f) => f.file!) : [];
    if ((!t && !ready.length) || sending.current || waiting || loader.current || loadError) return;
    if (withFiles && pending.some((f) => f.status === "uploading")) return;
    sending.current = true; followAnswer.current = true;
    const controller = new AbortController(); stream.current = controller;
    setBusy(true); setFailure(null); setDraft(""); setLive({ steps: [], actions: [] });
    if (withFiles) { pending.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)); setPending([]); }
    setMessages((list) => [...list, { id: `u${Date.now()}`, role: "user", content: t, attachments: ready }]);
    let current = sessionId;
    try {
      await readStream<JarvisEvent>("/api/jarvis/chat", { session_id: current, message: t, page: location.pathname, model, attachment_ids: ready.map((f) => f.id) }, (ev) => {
        if (!alive.current || controller.signal.aborted || stream.current !== controller) return;
        if (ev.type === "session" && ev.id !== current) {
          current = ev.id; skipLoad.current = true;
          const next = new URLSearchParams(params); next.set("session", String(ev.id)); next.delete("ask"); next.delete("go");
          setParams(next, { replace: true });
          sessions.reload();
        }
        if (ev.type === "step") setLive((l) => ({ ...l, steps: [...l.steps, ev.label] }));
        if (ev.type === "action") setLive((l) => ({ ...l, actions: [...l.actions, ev.action] }));
        if (ev.type === "done") setMessages((list) => [...list, ev.message]);
        if (ev.type === "error") throw new Error(ev.error);
      }, controller.signal, "Jarvis");
      if (alive.current && !controller.signal.aborted) sessions.reload();
    } catch (e) {
      if (alive.current && !controller.signal.aborted) {
        setFailure({ text: t, message: e instanceof Error ? e.message : "Jarvis could not answer." });
        if (current) void load(current, true);
      }
    } finally {
      if (stream.current === controller) {
        stream.current = null; sending.current = false;
        if (alive.current) { setBusy(false); setLive({ steps: [], actions: [] }); }
      }
    }
  }

  // A question from the Agency overview box is asked as the page opens, in a new conversation.
  useEffect(() => {
    const ask = params.get("ask");
    if (asked.current || !ask || !params.get("go") || sessionId) return;
    asked.current = true;
    void send(ask);
  });

  function newChat() {
    if (sending.current) return;
    setDrawerOpen(false);
    local.remove(LAST_KEY);
    const next = new URLSearchParams(params); next.delete("session"); next.delete("ask"); next.delete("go");
    setParams(next); setMessages([]); setFailure(null); setDraft("");
    if (window.matchMedia("(min-width: 761px)").matches) requestAnimationFrame(() => input.current?.focus());
  }

  function openConversation(id: number) {
    setDrawerOpen(false);
    if (id === sessionId || busy) return;
    setDraft("");
    const next = new URLSearchParams(params); next.set("session", String(id)); next.delete("ask"); next.delete("go");
    setParams(next);
  }

  /** Delete takes the conversation off your list (it is archived, not erased). */
  async function remove(id: number) {
    if (removing) return;
    setRemoving(id);
    try {
      await api(`/api/jarvis/sessions/${id}`, { method: "DELETE" });
      if (Number(local.get(LAST_KEY)) === id) local.remove(LAST_KEY);
      if (id === sessionId) newChat();
      sessions.reload();
    } catch { /* the row stays; the list reload shows the truth */ }
    finally { if (alive.current) setRemoving(null); }
  }

  /** The panel button hides the history on a laptop (so the chat takes the width) and opens it as a drawer on a phone. */
  function toggleRail() {
    if (window.matchMedia("(max-width: 760px)").matches) { setDrawerOpen(true); return; }
    setRailHidden((was) => { const next = !was; if (next) local.set(RAIL_KEY, "hidden"); else local.remove(RAIL_KEY); return next; });
  }

  /** Files from the picker, a paste or a drop: each uploads at once and shows in the message box. */
  function addFiles(list: FileList | File[] | null) {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const room = JARVIS_MAX_FILES - pending.length;
    const take = incoming.slice(0, Math.max(0, room));
    const extra = incoming.length - take.length;
    const rows: Pending[] = take.map((f, i) => ({
      key: `${Date.now()}-${i}-${f.name}`, name: f.name || "Pasted image", size: f.size,
      preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : undefined,
      // Photos are shrunk before upload, so only other files are held to the 4 MB limit here.
      status: !f.type.startsWith("image/") && f.size > JARVIS_MAX_BYTES ? "error" : "uploading",
      error: !f.type.startsWith("image/") && f.size > JARVIS_MAX_BYTES ? "Over 4 MB" : undefined,
    }));
    if (extra > 0) rows.push({ key: `${Date.now()}-extra`, name: `${extra} more ${extra === 1 ? "file" : "files"}`, size: 0, status: "error", error: `Up to ${JARVIS_MAX_FILES} files per message` });
    setPending((was) => [...was, ...rows]);
    rows.forEach((row, i) => {
      if (row.status !== "uploading") return;
      const named = take[i].name ? take[i] : new File([take[i]], `pasted-${Date.now()}.png`, { type: take[i].type });
      uploadAttachment(named)
        .then((file) => { if (alive.current) setPending((was) => was.map((p) => (p.key === row.key ? { ...p, status: "ready", file, name: file.name, size: file.size } : p))); })
        .catch((e) => { if (alive.current) setPending((was) => was.map((p) => (p.key === row.key ? { ...p, status: "error", error: e instanceof Error ? e.message : "Upload failed" } : p))); });
    });
  }

  function dropFile(key: string) {
    setPending((was) => {
      const gone = was.find((p) => p.key === key);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      if (gone?.file) void api(`/api/jarvis/attachments/${gone.file.id}`, { method: "DELETE" }).catch(() => {});
      return was.filter((p) => p.key !== key);
    });
  }

  function closeDrawer() { setDrawerOpen(false); drawerToggle.current?.focus(); }
  useEffect(() => {
    if (!drawerOpen) return;
    document.querySelector<HTMLElement>(".jarvis-chat .ac-rail")?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeDrawer(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // The composer grows with its text; CSS caps it at about eight lines.
  useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  const groups = useMemo(() => groupJarvisSessions(sessions.data || [], search), [sessions.data, search]);
  const current = sessions.data?.find((x) => x.id === sessionId);

  const models = settings.data?.models || [];
  const model = models.length ? startingModel(models, modelPick, settings.data?.default_model || "claude") : null;
  const uploading = pending.some((f) => f.status === "uploading");
  const readyCount = pending.filter((f) => f.status === "ready").length;
  const lastAssistant = messages.map((m) => m.role).lastIndexOf("assistant");
  const empty = !sessionId && !messages.length && !busy;
  // What Jarvis reads is in the empty chat; the subtitle speaks only when the AI it runs on is not connected.
  const notConnected = settings.data && !(settings.data.models || settings.data.choices).some((m) => m.connected) ? "No AI is connected yet. An owner adds a Claude or OpenAI key in Settings, Integrations." : undefined;

  const heading = current ? jarvisTitle(current) : sessionId ? "Conversation" : "New chat";

  return (
    <div className={["analyst-chat", "jarvis-chat", drawerOpen && "drawer-open", railHidden && "rail-hidden"].filter(Boolean).join(" ")}>
      <h1 className="sr-only">Jarvis</h1>
      <aside className="ac-rail" id="jarvis-conversations" aria-label="Jarvis conversations" tabIndex={-1}>
        <div className="ac-rail-head">
          <div className="ac-rail-top">
            <button type="button" className="ac-new" onClick={newChat} disabled={busy}><SquarePen aria-hidden="true" />New chat</button>
            <button type="button" className="ac-icon-btn ac-drawer-only" onClick={closeDrawer} aria-label="Close conversations"><X aria-hidden="true" /></button>
          </div>
          <label className="ac-search">
            <Search aria-hidden="true" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations" aria-label="Search conversations" type="search" />
          </label>
        </div>
        <nav className="ac-rail-list" aria-label="Conversation history">
          {sessions.loading && !sessions.data ? (
            <div className="ac-rail-skeleton" aria-label="Loading conversations">{[0, 1, 2, 3].map((i) => <span key={i} />)}</div>
          ) : sessions.error && !sessions.data ? (
            <p className="ac-rail-empty">Conversations could not load. <button type="button" className="ac-link-btn" onClick={sessions.reload}>Retry</button></p>
          ) : !groups.length ? (
            <p className="ac-rail-empty">{search.trim() ? `No conversations match "${search.trim()}".` : "Your chats with Jarvis show up here."}</p>
          ) : groups.map((g) => (
            <section className="ac-group" key={g.label}>
              <h3 className="ac-group-label">{g.label}</h3>
              <ul>
                {g.items.map((x) => (
                  <li key={x.id} className="jarvis-row">
                    <button type="button" className={x.id === sessionId ? "ac-row active" : "ac-row"} aria-current={x.id === sessionId ? "page" : undefined} onClick={() => openConversation(x.id)} disabled={busy && x.id !== sessionId}>
                      <strong title={jarvisTitle(x)}>{jarvisTitle(x)}</strong>
                      {x.running ? <span className="jarvis-row-running"><Loader2 className="spin" aria-hidden="true" />Answering</span> : <time dateTime={x.updated_at}>{jarvisWhen(x.updated_at)}</time>}
                    </button>
                    <button type="button" className="jarvis-row-delete" onClick={() => void remove(x.id)} disabled={removing === x.id || (busy && x.id === sessionId)} aria-label={`Delete "${jarvisTitle(x)}"`} title="Delete conversation"><Trash2 aria-hidden="true" /></button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>
        {admin && (
          <div className="jarvis-rail-foot">
            <Link className="jarvis-rail-settings" to="/settings#jarvis"><Settings2 aria-hidden="true" />Jarvis settings</Link>
          </div>
        )}
      </aside>
      {drawerOpen && <button type="button" className="ac-scrim" aria-label="Close conversations" tabIndex={-1} onClick={closeDrawer} />}

      <section className="ac-main" aria-label={heading}
        onDragOver={(e) => { if (Array.from(e.dataTransfer.types).includes("Files")) { e.preventDefault(); if (!dragging) setDragging(true); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false); }}
        onDrop={(e) => { if (!e.dataTransfer.files.length) return; e.preventDefault(); setDragging(false); if (!busy && !waiting) addFiles(e.dataTransfer.files); }}>
        {dragging && <div className="jarvis-drop" aria-hidden="true"><Paperclip />Drop files to attach them</div>}
        <header className="ac-head">
          <button ref={drawerToggle} type="button" className="ac-icon-btn jarvis-rail-toggle" onClick={toggleRail} aria-label="Conversations" title={railHidden ? "Show conversations" : "Hide conversations"} aria-expanded={drawerOpen || !railHidden} aria-controls="jarvis-conversations"><PanelLeft aria-hidden="true" /></button>
          <div className="ac-head-title"><h2 title={heading}>{heading}</h2></div>
          <div className="ac-head-actions">
            <button type="button" className={railHidden ? "ac-icon-btn" : "ac-icon-btn ac-drawer-only"} onClick={newChat} disabled={busy} aria-label="New chat" title="New chat"><SquarePen aria-hidden="true" /></button>
          </div>
        </header>
          <div className="ac-scroll" ref={scroller} onScroll={(e) => {
            const el = e.currentTarget;
            followAnswer.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
          }}>
            {loading ? (
              <div className="ac-thread ac-thread-skeleton" aria-label="Loading conversation"><span className="ac-sk-user" /><span className="ac-sk-line" /><span className="ac-sk-line short" /></div>
            ) : loadError ? (
              <div className="ac-thread"><ErrorState message={loadError} onRetry={() => sessionId && void load(sessionId)} /></div>
            ) : empty ? (
              <div className="ac-welcome">
                <AnalystMark />
                <h2>Ask Jarvis about the agency</h2>
                <p>Jarvis reads the same pages you can: clients, ads, health, calls, the work queue, approvals, CRM and the knowledge base. It can add tasks to the work queue. It does not change ads, budgets or settings.</p>
                <div className="ac-starters">
                  {JARVIS_STARTERS.map((s) => (
                    <button key={s} type="button" className="ac-starter" onClick={() => void send(s)}><span>{s}</span><ArrowRight aria-hidden="true" /></button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="ac-thread" role="log" aria-label="Conversation with Jarvis" aria-busy={busy}>
                {/* The last failed reply is shown once, in the No answer box with Retry. */}
                {messages.filter((m, i) => !(m.failed && i === messages.length - 1 && failure)).map((m, i) => (
                  <ChatMessage key={m.id} message={{ id: m.id, role: m.role, content_text: m.content, created_at: m.created_at }} last={i === lastAssistant && !busy} hideEmpty={Boolean(m.attachments?.length)}>
                    {m.role === "user" && !!m.attachments?.length && <SentFiles files={m.attachments} />}
                    {m.role === "assistant" && (m.actions || []).map((a) => <ActionCard key={`${a.type}${a.id}`} action={a} />)}
                    {m.role === "assistant" && !m.failed && <Steps steps={m.steps || []} by={modelLabel(m.model) || providerLabel(m.provider, m.model)} />}
                  </ChatMessage>
                ))}
                {waiting && !busy && (
                  <div className="ac-msg ac-msg-assistant ads-ai-working" aria-live="polite">
                    <AnalystMark />
                    <div className="ac-reply"><ul className="ads-ai-live"><li className="now"><Loader2 className="spin" aria-hidden="true" />Still working on this answer. It shows up here when it is done.</li></ul></div>
                  </div>
                )}
                {busy && (
                  <div className="ac-msg ac-msg-assistant ads-ai-working" aria-live="polite">
                    <AnalystMark />
                    <div className="ac-reply">
                      <ul className="ads-ai-live">
                        {live.steps.map((s, i) => <li key={i} className={i === live.steps.length - 1 ? "now" : ""}>{i === live.steps.length - 1 ? <Loader2 className="spin" aria-hidden="true" /> : <Check aria-hidden="true" />}{s}</li>)}
                        {!live.steps.length && <li className="now"><Loader2 className="spin" aria-hidden="true" />Thinking</li>}
                      </ul>
                      {live.actions.map((a) => <ActionCard key={`${a.type}${a.id}`} action={a} />)}
                    </div>
                  </div>
                )}
                {failure && (
                  <div className="ac-error" role="alert">
                    <AlertCircle aria-hidden="true" />
                    <p><strong>No answer.</strong> {failure.message}</p>
                    <button type="button" className="ac-retry" onClick={() => void send(failure.text, false)}><RotateCw aria-hidden="true" />Retry</button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="ac-composer-wrap">
            <form className={dragging ? "ac-composer jarvis-composer is-dropping" : "ac-composer jarvis-composer"} onSubmit={(e) => { e.preventDefault(); void send(draft); }}>
              {pending.length > 0 && (
                <ul className="jarvis-files" aria-label="Attached files">
                  {pending.map((f) => (
                    <li key={f.key} className={`jarvis-file is-${f.status}`}>
                      {f.preview ? <img src={f.preview} alt="" /> : <span className="jarvis-file-icon">{/\.(csv|tsv|xlsx?)$/i.test(f.name) ? <FileSpreadsheet aria-hidden="true" /> : <FileText aria-hidden="true" />}</span>}
                      <span className="jarvis-file-text"><strong title={f.name}>{f.name}</strong><small>{f.status === "uploading" ? "Uploading…" : f.status === "error" ? f.error : fileSizeLabel(f.size)}</small></span>
                      {f.status === "uploading" ? <Loader2 className="spin jarvis-file-spin" aria-label="Uploading" /> : <button type="button" className="jarvis-file-remove" onClick={() => dropFile(f.key)} aria-label={`Remove ${f.name}`}><X aria-hidden="true" /></button>}
                    </li>
                  ))}
                </ul>
              )}
              <label htmlFor="jarvis-message" className="sr-only">Message Jarvis</label>
              <textarea ref={input} id="jarvis-message" className="ac-input" rows={1} value={draft} maxLength={4000} disabled={busy || waiting || loading || !!loadError}
                onChange={(e) => setDraft(e.target.value)}
                onPaste={(e) => { if (e.clipboardData.files.length) { e.preventDefault(); addFiles(e.clipboardData.files); } }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(draft); } }}
                placeholder={busy || waiting ? "Jarvis is working…" : "Ask Jarvis anything, or attach a screenshot or file…"} />
              <div className="jarvis-tools">
                <input ref={picker} type="file" multiple accept={JARVIS_ACCEPT} hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                <button type="button" className="jarvis-tool" onClick={() => picker.current?.click()} disabled={busy || waiting || pending.length >= JARVIS_MAX_FILES} aria-label="Attach files" title="Attach images, PDFs, spreadsheets or text files"><Paperclip aria-hidden="true" /></button>
                {models.length > 0 && model && (
                  <label className="jarvis-model" title="Which AI answers this message">
                    <span className="sr-only">Model</span>
                    <select value={model} disabled={busy} onChange={(e) => { setModelPick(e.target.value); local.set(MODEL_KEY, e.target.value); }}>
                      {groupModels(models).map((g) => (
                        <optgroup key={g.label} label={g.label}>
                          {g.models.map((m) => <option key={m.id} value={m.id} disabled={!m.connected}>{m.label}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    <ChevronDown aria-hidden="true" />
                  </label>
                )}
              </div>
              <button type="submit" className="ac-send" disabled={busy || waiting || loading || !!loadError || uploading || (!draft.trim() && !readyCount)} aria-label={uploading ? "Waiting for files to upload" : "Send message"}><ArrowUp aria-hidden="true" /></button>
            </form>
            <p className="ac-hint">{notConnected || "Enter to send, Shift+Enter for a new line."}</p>
          </div>
        </section>
    </div>
  );
}

/** The files sent with one of your messages: pictures as thumbnails, other files as chips. Each opens in a new tab. */
function SentFiles({ files }: { files: JarvisAttachment[] }) {
  return (
    <ul className="jarvis-sent" aria-label="Attached files">
      {files.map((f) => (
        <li key={f.id}>
          <a href={attachmentUrl(f.id)} target="_blank" rel="noreferrer" className={f.kind === "image" ? "jarvis-sent-image" : "jarvis-sent-file"} title={f.name}>
            {f.kind === "image" ? <img src={attachmentUrl(f.id)} alt={f.name} loading="lazy" /> : <>{/\.(csv|tsv|xlsx?)$/i.test(f.name) ? <FileSpreadsheet aria-hidden="true" /> : <FileText aria-hidden="true" />}<span><strong>{f.name}</strong><small>{f.kind === "pdf" ? "PDF" : "File"}, {fileSizeLabel(f.size)}</small></span></>}
          </a>
        </li>
      ))}
    </ul>
  );
}

/** What Jarvis read for this answer and which AI wrote it, folded away. */
function Steps({ steps, by }: { steps: string[]; by: string }) {
  if (!steps.length && !by) return null;
  return (
    <details className="ads-ai-steps">
      <summary>{steps.length ? `What Jarvis read${by ? `, answered by ${by}` : ""}` : `Answered by ${by}`}</summary>
      {steps.length > 0 && <ul>{steps.map((s, i) => <li key={i}>{s}</li>)}</ul>}
    </details>
  );
}

/** Something Jarvis did, with a link to where it is. */
function ActionCard({ action }: { action: JarvisAction }) {
  return (
    <div className="ads-ai-proposal is-applied">
      <div className="ads-ai-proposal-main">
        <span className="ads-ai-proposal-kind"><ClipboardCheck aria-hidden="true" /> Task added</span>
        <strong>{action.title}</strong>
        {action.due_date && <span className="muted">Due {new Date(`${action.due_date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
      </div>
      <div className="ads-ai-proposal-actions"><Link className="text-button" to={action.to}>Open work queue</Link></div>
    </div>
  );
}
